import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Search,
  Trash2,
  Pencil,
  PlusCircle,
  Upload,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  RefreshCcw,
  X,
  Check,
  ChevronLeft,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  Info,
  Unlock,
  UserCheck,
} from 'lucide-react';
import { useTheme } from 'contexts/ThemeContext';
import {
  listProxies,
  getProxyStats,
  deleteProxy,
  bulkDeleteProxies,
  enableProxy,
  disableProxy,
  releaseProxy,
  uploadProxy,
  bulkUploadProxies,
  updateProxy,
  parseProxyString,
  buildProxyString,
  type Proxy,
  type ProxyUpload,
} from 'services/proxy/proxyService';
import Spinner from 'components/common/Spinner';
import { logger } from 'utils/logger';
import type {
  ProxyStats,
  ProxySortBy,
  ProxySortOrder,
} from './proxies-manager.types';
import {
  INITIAL_PROXY_FORM_FIELDS,
  EMPTY_PROXY_STATS,
  DEFAULT_SORT_BY,
  DEFAULT_SORT_ORDER,
  DEFAULT_ITEMS_PER_PAGE,
  MAX_VISIBLE_PAGES,
} from './proxies-manager.constants';
import {
  buildProxyFilters,
  calculateStatsFromProxies,
  countBulkUploadLines,
  formatLastUsed,
  formatStatus,
  getErrorMessage,
  getPaginationRange,
  getProxiesThemeColors,
  getStatusBadgeStyle,
  parseBulkUploadText,
  proxyToFormFields,
} from './proxies-manager.utils';

export default function ProxiesManager() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [proxies, setProxies] = useState<Proxy[]>([]);
  const [stats, setStats] = useState<ProxyStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [selectedProxies, setSelectedProxies] = useState<Set<string>>(new Set());
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [editingProxy, setEditingProxy] = useState<Proxy | null>(null);
  const [deletingProxyId, setDeletingProxyId] = useState<string | null>(null);

  // Pagination and filters
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [countryFilter, setCountryFilter] = useState<string>('');
  const [providerFilter, setProviderFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<ProxySortBy>(DEFAULT_SORT_BY);
  const [sortOrder, setSortOrder] = useState<ProxySortOrder>(DEFAULT_SORT_ORDER);

  // Form states for individual fields (UI only)
  const [proxyFormFields, setProxyFormFields] = useState(INITIAL_PROXY_FORM_FIELDS);

  const [bulkUploadText, setBulkUploadText] = useState('');

  // Theme color tokens
  const theme = getProxiesThemeColors(isDark);
  const {
    mainBg,
    cardBorder,
    titleColor,
    subtitleColor,
    tableHeaderBg,
    tableBorderColor,
    inputBg,
    inputBorder,
    inputText,
    buttonBg,
    buttonText,
    paginationBg,
    paginationBorder,
    paginationText,
    modalBackdrop,
    modalBg,
    countryBadge: countryBadgeStyle,
    providerBadge: providerBadgeStyle,
    filterChipSearch,
    filterChipStatus,
    filterChipCountry,
    filterChipProvider,
    warningBoxBg,
    warningBoxBorder,
    warningBoxTitle,
    warningBoxText,
    warningBoxIcon,
    infoBoxBg,
    infoBoxBorder,
    infoBoxTitle,
    infoBoxText,
    infoBoxIcon,
    infoBoxCodeBg,
    infoBoxCodeBorder,
    infoBoxCodeText,
  } = theme;

  // Fetch proxies and stats
  const fetchData = async () => {
    try {
      setLoading(true);

      const filters = buildProxyFilters({
        currentPage,
        itemsPerPage,
        searchQuery,
        statusFilter,
        countryFilter,
        providerFilter,
        sortBy,
        sortOrder,
      });

      // Fetch proxies (required)
      const proxiesData = await listProxies(filters);
      // Validate response structure
      if (!proxiesData || !proxiesData.data || !Array.isArray(proxiesData.data)) {
        logger.error('Invalid proxies response — shape:', { type: typeof proxiesData?.data, hasData: !!proxiesData?.data });
        setProxies([]);
        setTotalPages(1);
        setTotalItems(0);
        setCurrentPage(1);
        setStats({ ...EMPTY_PROXY_STATS });
        return;
      }

      setProxies(proxiesData.data);
      setTotalPages(proxiesData.pagination?.total_pages || 1);
      setTotalItems(proxiesData.pagination?.total_items || 0);
      setCurrentPage(proxiesData.pagination?.current_page || 1);

      // Fetch stats (optional - don't fail if this errors)
      try {
        const statsData = await getProxyStats();
        setStats(statsData);
      } catch (statsError) {
        logger.error('Failed to fetch stats API, calculating from current page data:', statsError);
        // Calculate stats from the current page data
        setStats(
          calculateStatsFromProxies(
            proxiesData.data,
            proxiesData.pagination?.total_items
          )
        );
      }
    } catch (error) {
      logger.error('Failed to fetch proxies:', error);
      toast.error(getErrorMessage(error, t('admin.proxies.errors.fetchFailed')));
      setProxies([]);
      setStats({ ...EMPTY_PROXY_STATS });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, itemsPerPage, searchQuery, statusFilter, countryFilter, providerFilter, sortBy, sortOrder]);

  // Handle single proxy upload
  const handleUploadProxy = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setActionLoading('upload');

      // Build proxy string from form fields
      const proxyString = buildProxyString(
        proxyFormFields.ip,
        parseInt(proxyFormFields.port),
        proxyFormFields.username || undefined,
        proxyFormFields.password || undefined
      );

      const uploadData: ProxyUpload = {
        proxy: proxyString,
        country: proxyFormFields.country.toUpperCase(),
        provider: proxyFormFields.provider || undefined,
      };

      await uploadProxy(uploadData);
      toast.success(t('admin.proxies.messages.uploadSuccess'));
      setShowUploadModal(false);
      setProxyFormFields(INITIAL_PROXY_FORM_FIELDS);

      // Refresh the list
      await fetchData();
    } catch (error) {
      logger.error('Upload proxy failed:', error);
      toast.error(getErrorMessage(error, t('admin.proxies.errors.uploadFailed')));
    } finally {
      setActionLoading(null);
    }
  };

  // Handle bulk upload
  const handleBulkUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setActionLoading('bulkUpload');

      const proxies: ProxyUpload[] = parseBulkUploadText(bulkUploadText);

      const result = await bulkUploadProxies(proxies);

      const successCount = result.success || 0;
      const failedCount = result.failed || 0;
      const errors = result.errors || [];

      toast.success(
        t('admin.proxies.messages.bulkUploadSuccess', {
          success: successCount,
          failed: failedCount,
        })
      );

      if (errors.length > 0) {
        errors.forEach(err => {
          toast.error(`Upload error: ${err}`, { duration: 3000 });
        });
      }

      setShowBulkUploadModal(false);
      setBulkUploadText('');

      // Refresh the list
      await fetchData();
    } catch (error) {
      logger.error('Bulk upload failed:', error);
      toast.error(getErrorMessage(error, t('admin.proxies.errors.bulkUploadFailed')));
    } finally {
      setActionLoading(null);
    }
  };

  // Handle proxy actions
  const handleEnableProxy = async (proxyId: string) => {
    try {
      setActionLoading(proxyId);
      await enableProxy(proxyId);
      toast.success(t('admin.proxies.messages.enableSuccess'));
      await fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setActionLoading(null);
    }
  };

  const handleDisableProxy = async (proxyId: string) => {
    try {
      setActionLoading(proxyId);
      await disableProxy(proxyId);
      toast.success(t('admin.proxies.messages.disableSuccess'));
      await fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setActionLoading(null);
    }
  };

  const handleReleaseProxy = async (proxyId: string) => {
    try {
      setActionLoading(proxyId);
      await releaseProxy(proxyId);
      toast.success(t('admin.proxies.messages.releaseSuccess'));
      await fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteProxy = (proxyId: string) => {
    setDeletingProxyId(proxyId);
    setShowDeleteModal(true);
  };

  const confirmDeleteProxy = async () => {
    if (!deletingProxyId) return;

    try {
      setActionLoading('delete');
      await deleteProxy(deletingProxyId);
      toast.success(t('admin.proxies.messages.deleteSuccess'));
      setShowDeleteModal(false);
      setDeletingProxyId(null);
      await fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkDelete = () => {
    if (selectedProxies.size === 0) {
      toast.warning(t('admin.proxies.warnings.noSelection'));
      return;
    }

    setShowBulkDeleteModal(true);
  };

  const confirmBulkDelete = async () => {
    try {
      setActionLoading('bulkDelete');
      const result = await bulkDeleteProxies(Array.from(selectedProxies));
      toast.success(
        t('admin.proxies.messages.bulkDeleteSuccess', { count: result.deletedCount || 0 })
      );
      setShowBulkDeleteModal(false);
      setSelectedProxies(new Set());
      await fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setActionLoading(null);
    }
  };

  // Handle edit proxy
  const handleEditProxy = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingProxy) return;

    try {
      setActionLoading('edit');

      const proxyString = buildProxyString(
        proxyFormFields.ip,
        parseInt(proxyFormFields.port),
        proxyFormFields.username || undefined,
        proxyFormFields.password || undefined
      );

      const updates: ProxyUpload = {
        proxy: proxyString,
        country: proxyFormFields.country.toUpperCase(),
        provider: proxyFormFields.provider || undefined,
      };

      await updateProxy(editingProxy._id, updates);
      toast.success(t('admin.proxies.messages.updateSuccess'));
      setShowEditModal(false);
      setEditingProxy(null);
      await fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setActionLoading(null);
    }
  };

  const openEditModal = (proxy: Proxy) => {
    setEditingProxy(proxy);

    const parsed = parseProxyString(proxy.proxy);
    setProxyFormFields(proxyToFormFields(proxy, parsed));

    setShowEditModal(true);
  };

  // Handle checkbox selection
  const toggleProxySelection = (proxyId: string) => {
    const newSelection = new Set(selectedProxies);
    if (newSelection.has(proxyId)) {
      newSelection.delete(proxyId);
    } else {
      newSelection.add(proxyId);
    }
    setSelectedProxies(newSelection);
  };

  const toggleSelectAll = () => {
    if (selectedProxies.size === proxies.length) {
      setSelectedProxies(new Set());
    } else {
      setSelectedProxies(new Set(proxies.map(p => p._id)));
    }
  };

  // Filter handlers
  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleCountryChange = (value: string) => {
    setCountryFilter(value);
    setCurrentPage(1);
  };

  const handleProviderChange = (value: string) => {
    setProviderFilter(value);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('');
    setCountryFilter('');
    setProviderFilter('');
    setSortBy(DEFAULT_SORT_BY);
    setSortOrder(DEFAULT_SORT_ORDER);
    setCurrentPage(1);
  };

  // Pagination handlers
  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Generate pagination items
  const renderPaginationItems = () => {
    const items = [];
    const { startPage, endPage } = getPaginationRange(currentPage, totalPages, MAX_VISIBLE_PAGES);

    if (startPage > 1) {
      items.push(
        <button
          key="first"
          onClick={() => handlePageChange(1)}
          className="px-3 py-1.5 text-xs rounded-md transition-colors"
          style={{ background: paginationBg, borderWidth: 1, borderStyle: 'solid', borderColor: paginationBorder, color: paginationText }}
        >
          1
        </button>
      );

      if (startPage > 2) {
        items.push(
          <span key="ellipsis-start" className="px-2" style={{ color: subtitleColor }}>...</span>
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className="px-3 py-1.5 text-xs rounded-md transition-colors"
          style={
            i === currentPage
              ? { background: '#007bff', color: '#FFFFFF', borderWidth: 1, borderStyle: 'solid', borderColor: '#007bff' }
              : { background: paginationBg, borderWidth: 1, borderStyle: 'solid', borderColor: paginationBorder, color: paginationText }
          }
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(
          <span key="ellipsis-end" className="px-2" style={{ color: subtitleColor }}>...</span>
        );
      }

      items.push(
        <button
          key="last"
          onClick={() => handlePageChange(totalPages)}
          className="px-3 py-1.5 text-xs rounded-md transition-colors"
          style={{ background: paginationBg, borderWidth: 1, borderStyle: 'solid', borderColor: paginationBorder, color: paginationText }}
        >
          {totalPages}
        </button>
      );
    }

    return items;
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="large" text={t('admin.loading.proxies')} />
      </div>
    );
  }

  const hasActiveFilters = searchQuery || statusFilter || countryFilter || providerFilter;

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid gap-3 md:grid-cols-4">
        <div className="rounded-lg p-3" style={{ background: mainBg, borderWidth: 1, borderStyle: 'solid', borderColor: cardBorder }}>
          <div className="text-xs font-medium" style={{ color: subtitleColor }}>
            {t('admin.proxies.stats.total')}
          </div>
          <div className="text-xl font-bold mt-1" style={{ color: titleColor }}>{stats?.total || 0}</div>
        </div>
        <div className="rounded-lg p-3" style={{ background: mainBg, borderWidth: 1, borderStyle: 'solid', borderColor: cardBorder }}>
          <div className="text-xs font-medium" style={{ color: subtitleColor }}>
            {t('admin.proxies.stats.available')}
          </div>
          <div className="text-xl font-bold mt-1" style={{ color: isDark ? '#5EE9B5' : '#16A34A' }}>{stats?.available || 0}</div>
        </div>
        <div className="rounded-lg p-3" style={{ background: mainBg, borderWidth: 1, borderStyle: 'solid', borderColor: cardBorder }}>
          <div className="text-xs font-medium" style={{ color: subtitleColor }}>
            {t('admin.proxies.stats.inUse')}
          </div>
          <div className="text-xl font-bold mt-1" style={{ color: isDark ? '#60A5FA' : '#2563EB' }}>{stats?.inUse || 0}</div>
        </div>
        <div className="rounded-lg p-3" style={{ background: mainBg, borderWidth: 1, borderStyle: 'solid', borderColor: cardBorder }}>
          <div className="text-xs font-medium" style={{ color: subtitleColor }}>
            {t('admin.proxies.stats.disabled')}
          </div>
          <div className="text-xl font-bold mt-1" style={{ color: subtitleColor }}>{stats?.disabled || 0}</div>
        </div>
      </div>

      {/* Upload Actions Card */}
      <div className="rounded-lg p-5" style={{ background: mainBg, borderWidth: 1, borderStyle: 'solid', borderColor: cardBorder }}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-xs font-semibold" style={{ color: titleColor }}>
              {t('admin.proxies.uploadSection.title', 'Proxy Management')}
            </h3>
            <p className="text-xs mt-0.5" style={{ color: subtitleColor }}>
              {t('admin.proxies.uploadSection.description', 'Add new proxies individually or in bulk')}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowUploadModal(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-xs font-medium"
          >
            <PlusCircle size={14} />
            <span>{t('admin.proxies.buttons.uploadSingle', 'Upload proxy')}</span>
          </button>

          <button
            onClick={() => setShowBulkUploadModal(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-xs font-medium"
          >
            <Upload size={14} />
            <span>{t('admin.proxies.buttons.bulkUpload', 'Bulk upload proxy')}</span>
          </button>

          {selectedProxies.size > 0 && (
            <button
              onClick={handleBulkDelete}
              disabled={actionLoading === 'bulkDelete'}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 size={14} />
              <span>{t('admin.proxies.buttons.bulkDelete', 'Delete Selected')} ({selectedProxies.size})</span>
            </button>
          )}
        </div>
      </div>

      {/* Main Content Card */}
      <div className="rounded-lg" style={{ background: mainBg, borderWidth: 1, borderStyle: 'solid', borderColor: cardBorder }}>
        {/* Search and Filters */}
        <div className="p-5 space-y-3" style={{ borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: tableBorderColor }}>
          <div className="flex flex-col lg:flex-row gap-2 items-stretch lg:items-center">
            {/* Search Input */}
            <div className="lg:flex-[2] min-w-0">
              <div className="relative">
                <input
                  type="text"
                  placeholder={t('admin.proxies.filters.searchPlaceholder', 'Search by IP, country, or provider...')}
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  style={{ background: inputBg, borderWidth: 1, borderStyle: 'solid', borderColor: inputBorder, color: inputText }}
                />
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: subtitleColor }} />
              </div>
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="lg:w-32 px-2 py-2 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ background: inputBg, borderWidth: 1, borderStyle: 'solid', borderColor: inputBorder, color: inputText }}
            >
              <option value="">All Statuses</option>
              <option value="available">Available</option>
              <option value="in-use">In Use</option>
              <option value="disabled">Disabled</option>
            </select>

            {/* Country Filter */}
            <input
              type="text"
              placeholder="Country (US, UK...)"
              value={countryFilter}
              onChange={(e) => handleCountryChange(e.target.value.toUpperCase())}
              maxLength={2}
              className="lg:w-32 px-2 py-2 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ background: inputBg, borderWidth: 1, borderStyle: 'solid', borderColor: inputBorder, color: inputText }}
            />

            {/* Provider Filter */}
            <input
              type="text"
              placeholder="Provider"
              value={providerFilter}
              onChange={(e) => handleProviderChange(e.target.value)}
              className="lg:w-32 px-2 py-2 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ background: inputBg, borderWidth: 1, borderStyle: 'solid', borderColor: inputBorder, color: inputText }}
            />

            {/* Sort By */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as ProxySortBy)}
              className="lg:w-32 px-2 py-2 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ background: inputBg, borderWidth: 1, borderStyle: 'solid', borderColor: inputBorder, color: inputText }}
            >
              <option value="createdAt">Created Date</option>
              <option value="lastUsed">Last Used</option>
              <option value="country">Country</option>
              <option value="status">Status</option>
            </select>

            {/* Sort Order */}
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="lg:w-20 px-2 py-2 text-xs rounded-md transition-colors flex items-center gap-1 whitespace-nowrap justify-center"
              style={{ background: buttonBg, borderWidth: 1, borderStyle: 'solid', borderColor: inputBorder, color: buttonText }}
              title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
            >
              {sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
              {sortOrder === 'asc' ? 'A-Z' : 'Z-A'}
            </button>

            {/* Clear Filters Button */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="lg:w-24 px-2 py-2 text-xs rounded-md transition-colors flex items-center gap-1 whitespace-nowrap justify-center"
                style={{ background: isDark ? '#2A1215' : '#FEF2F2', borderWidth: 1, borderStyle: 'solid', borderColor: isDark ? '#991B1B' : '#FCA5A5', color: isDark ? '#F87171' : '#DC2626' }}
              >
                <XCircle size={14} />
                Clear
              </button>
            )}
          </div>

          {/* Active Filters Info */}
          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2 items-center text-xs" style={{ color: subtitleColor }}>
              <span className="font-medium">Active filters:</span>
              {searchQuery && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md" style={filterChipSearch}>
                  Search: "{searchQuery}"
                  <button onClick={() => setSearchQuery('')} className="hover:opacity-80">
                    <X size={12} />
                  </button>
                </span>
              )}
              {statusFilter && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md" style={filterChipStatus}>
                  Status: {statusFilter}
                  <button onClick={() => setStatusFilter('')} className="hover:opacity-80">
                    <X size={12} />
                  </button>
                </span>
              )}
              {countryFilter && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md" style={filterChipCountry}>
                  Country: {countryFilter}
                  <button onClick={() => setCountryFilter('')} className="hover:opacity-80">
                    <X size={12} />
                  </button>
                </span>
              )}
              {providerFilter && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md" style={filterChipProvider}>
                  Provider: {providerFilter}
                  <button onClick={() => setProviderFilter('')} className="hover:opacity-80">
                    <X size={12} />
                  </button>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Table Controls */}
        <div className="px-5 py-3 flex justify-between items-center" style={{ borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: tableBorderColor }}>
          <div className="text-xs" style={{ color: subtitleColor }}>
            Showing {proxies.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} proxies
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs" style={{ color: subtitleColor }}>Rows per page:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(parseInt(e.target.value));
                setCurrentPage(1);
              }}
              disabled={loading}
              className="px-2 py-1 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 disabled:opacity-50"
              style={{ background: inputBg, borderWidth: 1, borderStyle: 'solid', borderColor: inputBorder, color: inputText }}
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
          </div>
        </div>

        {/* Proxies Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead style={{ background: tableHeaderBg, borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: tableBorderColor }}>
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedProxies.size === proxies.length && proxies.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: subtitleColor }}>
                  {t('admin.proxies.table.proxy', 'Proxy')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: subtitleColor }}>
                  {t('admin.proxies.table.country', 'Country')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: subtitleColor }}>
                  {t('admin.proxies.table.provider', 'Provider')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: subtitleColor }}>
                  {t('admin.proxies.table.status', 'Status')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: subtitleColor }}>
                  {t('admin.proxies.table.lastUsed', 'Last Used')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold" style={{ color: subtitleColor }}>
                  {t('admin.proxies.table.actions', 'Actions')}
                </th>
              </tr>
            </thead>
            <tbody style={{ background: mainBg }}>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center" style={{ color: subtitleColor }}>
                    <Spinner size="medium" text={t('admin.proxies.loading', 'Loading...')} />
                  </td>
                </tr>
              ) : proxies.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm" style={{ color: subtitleColor }}>
                    {hasActiveFilters
                      ? t('admin.proxies.noResultsFound', 'No proxies found matching your filters')
                      : t('admin.proxies.emptyState', 'No proxies found')
                    }
                  </td>
                </tr>
              ) : (
                proxies.map((proxy) => {
                  const parsed = parseProxyString(proxy.proxy);
                  return (
                    <tr
                      key={proxy._id}
                      className="transition-colors"
                      style={{ borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: tableBorderColor }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = isDark ? '#253247' : '#F8FAFC'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedProxies.has(proxy._id)}
                          onChange={() => toggleProxySelection(proxy._id)}
                          className="rounded"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs font-medium font-mono" style={{ color: titleColor }}>{parsed.ip}:{parsed.port}</div>
                        {parsed.username && (
                          <div className="text-xs mt-0.5" style={{ color: subtitleColor }}>
                            {parsed.username}:{parsed.password ? '****' : ''}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium"
                          style={countryBadgeStyle}
                        >
                          {proxy.country}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {proxy.provider ? (
                          <span
                            className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium"
                            style={providerBadgeStyle}
                          >
                            {proxy.provider}
                          </span>
                        ) : (
                          <span className="text-xs" style={{ color: subtitleColor }}>-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium"
                          style={{ ...getStatusBadgeStyle(proxy.status, isDark), borderWidth: 1, borderStyle: 'solid' }}
                        >
                          {proxy.status === 'available' && <CheckCircle2 size={12} className="mr-1" />}
                          {(proxy.status === 'in-use' || proxy.status === 'in_use') && <RefreshCcw size={12} className="mr-1" />}
                          {proxy.status === 'assigned_to_user' && <UserCheck size={12} className="mr-1" />}
                          {proxy.status === 'disabled' && <XCircle size={12} className="mr-1" />}
                          {formatStatus(proxy.status, t)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs" style={{ color: subtitleColor }}>
                        {formatLastUsed(proxy.lastUsed, t('admin.proxies.table.never', 'Never'))}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => openEditModal(proxy)}
                            className="px-2 py-1 rounded text-xs transition-colors"
                            style={{ color: isDark ? '#60A5FA' : '#2563EB' }}
                            title={t('admin.proxies.buttons.edit', 'Edit')}
                          >
                            <Pencil size={14} />
                          </button>

                          {(proxy.status === 'in-use' || proxy.status === 'in_use' || proxy.status === 'assigned_to_user') && (
                            <button
                              onClick={() => handleReleaseProxy(proxy._id)}
                              disabled={actionLoading === proxy._id}
                              className="px-2 py-1 rounded text-xs disabled:opacity-50 transition-colors"
                              style={{ color: isDark ? '#FB923C' : '#EA580C' }}
                              title={t('admin.proxies.buttons.release', 'Release')}
                            >
                              <Unlock size={14} />
                            </button>
                          )}

                          {proxy.status === 'disabled' ? (
                            <button
                              onClick={() => handleEnableProxy(proxy._id)}
                              disabled={actionLoading === proxy._id}
                              className="px-2 py-1 rounded text-xs disabled:opacity-50 transition-colors"
                              style={{ color: isDark ? '#5EE9B5' : '#16A34A' }}
                              title={t('admin.proxies.buttons.enable', 'Enable')}
                            >
                              <CheckCircle2 size={14} />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleDisableProxy(proxy._id)}
                              disabled={actionLoading === proxy._id}
                              className="px-2 py-1 rounded text-xs disabled:opacity-50 transition-colors"
                              style={{ color: isDark ? '#FBBF24' : '#CA8A04' }}
                              title={t('admin.proxies.buttons.disable', 'Disable')}
                            >
                              <XCircle size={14} />
                            </button>
                          )}

                          <button
                            onClick={() => handleDeleteProxy(proxy._id)}
                            disabled={actionLoading === proxy._id}
                            className="px-2 py-1 rounded text-xs disabled:opacity-50 transition-colors"
                            style={{ color: isDark ? '#F87171' : '#DC2626' }}
                            title={t('admin.proxies.buttons.delete', 'Delete')}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-5 py-3 flex items-center justify-between flex-wrap gap-2" style={{ borderTopWidth: 1, borderTopStyle: 'solid', borderTopColor: tableBorderColor }}>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1 || loading}
              className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: paginationBg, borderWidth: 1, borderStyle: 'solid', borderColor: paginationBorder, color: paginationText }}
            >
              <ChevronLeft size={14} />
              {t('admin.pagination.previous', 'Previous')}
            </button>

            <div className="flex items-center gap-1">
              {renderPaginationItems()}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || loading}
              className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: paginationBg, borderWidth: 1, borderStyle: 'solid', borderColor: paginationBorder, color: paginationText }}
            >
              {t('admin.pagination.next', 'Next')}
              <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && ReactDOM.createPortal(
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: modalBackdrop, backdropFilter: 'blur(4px)' }}>
          <div className="rounded-lg max-w-lg w-full shadow-xl" style={{ background: modalBg }}>
            {/* Modal Header */}
            <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: tableBorderColor }}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: isDark ? '#0C1A2E' : '#DBEAFE' }}>
                  <PlusCircle size={14} style={{ color: isDark ? '#60A5FA' : '#2563EB' }} />
                </div>
                <div>
                  <h3 className="text-xs font-semibold" style={{ color: titleColor }}>
                    {t('admin.proxies.modals.upload.title', 'Add New Proxy')}
                  </h3>
                  <p className="text-xs" style={{ color: subtitleColor }}>
                    {t('admin.proxies.modals.upload.subtitle', 'Enter proxy details below')}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowUploadModal(false)}
                className="transition-colors p-1"
                style={{ color: subtitleColor }}
              >
                <X size={14} />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleUploadProxy} className="p-5">
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: subtitleColor }}>
                      {t('admin.proxies.modals.upload.ip', 'IP Address')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={proxyFormFields.ip}
                      onChange={(e) => setProxyFormFields({ ...proxyFormFields, ip: e.target.value })}
                      className="w-full px-2 py-1.5 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      style={{ background: inputBg, borderWidth: 1, borderStyle: 'solid', borderColor: inputBorder, color: inputText }}
                      placeholder="192.168.1.1"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: subtitleColor }}>
                      {t('admin.proxies.modals.upload.port', 'Port')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      required
                      value={proxyFormFields.port}
                      onChange={(e) => setProxyFormFields({ ...proxyFormFields, port: e.target.value })}
                      className="w-full px-2 py-1.5 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      style={{ background: inputBg, borderWidth: 1, borderStyle: 'solid', borderColor: inputBorder, color: inputText }}
                      placeholder="8080"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: subtitleColor }}>
                      {t('admin.proxies.modals.upload.username', 'Username')}
                    </label>
                    <input
                      type="text"
                      value={proxyFormFields.username}
                      onChange={(e) => setProxyFormFields({ ...proxyFormFields, username: e.target.value })}
                      className="w-full px-2 py-1.5 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      style={{ background: inputBg, borderWidth: 1, borderStyle: 'solid', borderColor: inputBorder, color: inputText }}
                      placeholder="Optional"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: subtitleColor }}>
                      {t('admin.proxies.modals.upload.password', 'Password')}
                    </label>
                    <input
                      type="password"
                      value={proxyFormFields.password}
                      onChange={(e) => setProxyFormFields({ ...proxyFormFields, password: e.target.value })}
                      className="w-full px-2 py-1.5 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      style={{ background: inputBg, borderWidth: 1, borderStyle: 'solid', borderColor: inputBorder, color: inputText }}
                      placeholder="Optional"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: subtitleColor }}>
                      {t('admin.proxies.modals.upload.country', 'Country Code')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={proxyFormFields.country}
                      onChange={(e) => setProxyFormFields({ ...proxyFormFields, country: e.target.value.toUpperCase() })}
                      className="w-full px-2 py-1.5 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase"
                      style={{ background: inputBg, borderWidth: 1, borderStyle: 'solid', borderColor: inputBorder, color: inputText }}
                      placeholder="US"
                      maxLength={2}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: subtitleColor }}>
                      {t('admin.proxies.modals.upload.provider', 'Provider')}
                    </label>
                    <input
                      type="text"
                      value={proxyFormFields.provider}
                      onChange={(e) => setProxyFormFields({ ...proxyFormFields, provider: e.target.value })}
                      className="w-full px-2 py-1.5 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      style={{ background: inputBg, borderWidth: 1, borderStyle: 'solid', borderColor: inputBorder, color: inputText }}
                      placeholder="Optional"
                    />
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex gap-2 justify-end mt-4 pt-3" style={{ borderTopWidth: 1, borderTopStyle: 'solid', borderTopColor: tableBorderColor }}>
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
                  style={{ background: buttonBg, borderWidth: 1, borderStyle: 'solid', borderColor: inputBorder, color: buttonText }}
                >
                  {t('admin.proxies.modals.upload.cancel', 'Cancel')}
                </button>
                <button
                  type="submit"
                  disabled={actionLoading === 'upload'}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                >
                  {actionLoading === 'upload' ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      <span>{t('admin.proxies.modals.upload.uploading', 'Adding...')}</span>
                    </>
                  ) : (
                    <>
                      <Check size={14} />
                      <span>{t('admin.proxies.modals.upload.submit', 'Add Proxy')}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && ReactDOM.createPortal(
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: modalBackdrop, backdropFilter: 'blur(4px)' }}>
          <div className="rounded-lg max-w-3xl w-full shadow-xl" style={{ background: modalBg }}>
            {/* Modal Header */}
            <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: tableBorderColor }}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: isDark ? '#0D2818' : '#DCFCE7' }}>
                  <Upload size={14} style={{ color: isDark ? '#5EE9B5' : '#16A34A' }} />
                </div>
                <div>
                  <h3 className="text-xs font-semibold" style={{ color: titleColor }}>
                    {t('admin.proxies.modals.bulkUpload.title', 'Bulk Upload Proxies')}
                  </h3>
                  <p className="text-xs" style={{ color: subtitleColor }}>
                    {t('admin.proxies.modals.bulkUpload.subtitle', 'Upload multiple proxies at once')}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowBulkUploadModal(false)}
                className="transition-colors p-1"
                style={{ color: subtitleColor }}
              >
                <X size={14} />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleBulkUpload} className="p-5">
              <div className="space-y-3">
                <div className="rounded-md p-3" style={{ background: infoBoxBg, borderWidth: 1, borderStyle: 'solid', borderColor: infoBoxBorder }}>
                  <div className="flex gap-2">
                    <Info size={14} className="mt-0.5 flex-shrink-0" style={{ color: infoBoxIcon }} />
                    <div>
                      <h4 className="text-xs font-medium mb-1" style={{ color: infoBoxTitle }}>
                        {t('admin.proxies.modals.bulkUpload.instructions', 'Format Instructions')}
                      </h4>
                      <p className="text-xs mb-1.5" style={{ color: infoBoxText }}>
                        {t('admin.proxies.modals.bulkUpload.format', 'Enter one proxy per line in the following formats:')}
                      </p>
                      <div className="rounded p-2 font-mono text-xs space-y-0.5" style={{ background: infoBoxCodeBg, borderWidth: 1, borderStyle: 'solid', borderColor: infoBoxCodeBorder, color: infoBoxCodeText }}>
                        <div>username:password@ip:port COUNTRY PROVIDER</div>
                        <div>ip:port COUNTRY PROVIDER</div>
                        <div>username:password@ip:port COUNTRY</div>
                        <div>ip:port COUNTRY</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: subtitleColor }}>
                    {t('admin.proxies.modals.bulkUpload.textareaLabel', 'Proxies List')} <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    required
                    value={bulkUploadText}
                    onChange={(e) => setBulkUploadText(e.target.value)}
                    className="w-full px-2 py-1.5 text-xs rounded-md font-mono focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                    style={{ background: inputBg, borderWidth: 1, borderStyle: 'solid', borderColor: inputBorder, color: inputText }}
                    rows={12}
                    placeholder="user1:pass1@192.168.1.1:8080 US ProxyProvider1&#10;user2:pass2@192.168.1.2:8080 UK ProxyProvider1&#10;192.168.1.3:8080 FR&#10;user3:pass3@192.168.1.4:8080 CA ProxyProvider2"
                  />
                  <p className="text-xs mt-1" style={{ color: subtitleColor }}>
                    {countBulkUploadLines(bulkUploadText)} {t('admin.proxies.modals.bulkUpload.proxyCount', 'proxies ready to upload')}
                  </p>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex gap-2 justify-end mt-4 pt-3" style={{ borderTopWidth: 1, borderTopStyle: 'solid', borderTopColor: tableBorderColor }}>
                <button
                  type="button"
                  onClick={() => setShowBulkUploadModal(false)}
                  className="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
                  style={{ background: buttonBg, borderWidth: 1, borderStyle: 'solid', borderColor: inputBorder, color: buttonText }}
                >
                  {t('admin.proxies.modals.bulkUpload.cancel', 'Cancel')}
                </button>
                <button
                  type="submit"
                  disabled={actionLoading === 'bulkUpload'}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                >
                  {actionLoading === 'bulkUpload' ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      <span>{t('admin.proxies.modals.bulkUpload.uploading', 'Uploading...')}</span>
                    </>
                  ) : (
                    <>
                      <Upload size={14} />
                      <span>{t('admin.proxies.modals.bulkUpload.submit', 'Upload Proxies')}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Edit Modal */}
      {showEditModal && editingProxy && ReactDOM.createPortal(
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: modalBackdrop, backdropFilter: 'blur(4px)' }}>
          <div className="rounded-lg max-w-lg w-full shadow-xl" style={{ background: modalBg }}>
            {/* Modal Header */}
            <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: tableBorderColor }}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: isDark ? '#1C1507' : '#FFF7ED' }}>
                  <Pencil size={14} style={{ color: isDark ? '#FB923C' : '#EA580C' }} />
                </div>
                <div>
                  <h3 className="text-xs font-semibold" style={{ color: titleColor }}>
                    {t('admin.proxies.modals.edit.title', 'Edit Proxy')}
                  </h3>
                  <p className="text-xs" style={{ color: subtitleColor }}>
                    {t('admin.proxies.modals.edit.subtitle', 'Update proxy details')}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowEditModal(false)}
                className="transition-colors p-1"
                style={{ color: subtitleColor }}
              >
                <X size={14} />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleEditProxy} className="p-5">
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: subtitleColor }}>
                      {t('admin.proxies.modals.edit.ip', 'IP Address')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={proxyFormFields.ip}
                      onChange={(e) => setProxyFormFields({ ...proxyFormFields, ip: e.target.value })}
                      className="w-full px-2 py-1.5 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      style={{ background: inputBg, borderWidth: 1, borderStyle: 'solid', borderColor: inputBorder, color: inputText }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: subtitleColor }}>
                      {t('admin.proxies.modals.edit.port', 'Port')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      required
                      value={proxyFormFields.port}
                      onChange={(e) => setProxyFormFields({ ...proxyFormFields, port: e.target.value })}
                      className="w-full px-2 py-1.5 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      style={{ background: inputBg, borderWidth: 1, borderStyle: 'solid', borderColor: inputBorder, color: inputText }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: subtitleColor }}>
                      {t('admin.proxies.modals.edit.username', 'Username')}
                    </label>
                    <input
                      type="text"
                      value={proxyFormFields.username}
                      onChange={(e) => setProxyFormFields({ ...proxyFormFields, username: e.target.value })}
                      className="w-full px-2 py-1.5 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      style={{ background: inputBg, borderWidth: 1, borderStyle: 'solid', borderColor: inputBorder, color: inputText }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: subtitleColor }}>
                      {t('admin.proxies.modals.edit.password', 'Password')}
                    </label>
                    <input
                      type="password"
                      value={proxyFormFields.password}
                      onChange={(e) => setProxyFormFields({ ...proxyFormFields, password: e.target.value })}
                      className="w-full px-2 py-1.5 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      style={{ background: inputBg, borderWidth: 1, borderStyle: 'solid', borderColor: inputBorder, color: inputText }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: subtitleColor }}>
                      {t('admin.proxies.modals.edit.country', 'Country Code')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={proxyFormFields.country}
                      onChange={(e) => setProxyFormFields({ ...proxyFormFields, country: e.target.value.toUpperCase() })}
                      className="w-full px-2 py-1.5 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent uppercase"
                      style={{ background: inputBg, borderWidth: 1, borderStyle: 'solid', borderColor: inputBorder, color: inputText }}
                      maxLength={2}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: subtitleColor }}>
                      {t('admin.proxies.modals.edit.provider', 'Provider')}
                    </label>
                    <input
                      type="text"
                      value={proxyFormFields.provider}
                      onChange={(e) => setProxyFormFields({ ...proxyFormFields, provider: e.target.value })}
                      className="w-full px-2 py-1.5 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      style={{ background: inputBg, borderWidth: 1, borderStyle: 'solid', borderColor: inputBorder, color: inputText }}
                    />
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex gap-2 justify-end mt-4 pt-3" style={{ borderTopWidth: 1, borderTopStyle: 'solid', borderTopColor: tableBorderColor }}>
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
                  style={{ background: buttonBg, borderWidth: 1, borderStyle: 'solid', borderColor: inputBorder, color: buttonText }}
                >
                  {t('admin.proxies.modals.edit.cancel', 'Cancel')}
                </button>
                <button
                  type="submit"
                  disabled={actionLoading === 'edit'}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                >
                  {actionLoading === 'edit' ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      <span>{t('admin.proxies.modals.edit.saving', 'Saving...')}</span>
                    </>
                  ) : (
                    <>
                      <Check size={14} />
                      <span>{t('admin.proxies.modals.edit.submit', 'Save Changes')}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && ReactDOM.createPortal(
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: modalBackdrop, backdropFilter: 'blur(4px)' }}>
          <div className="rounded-lg max-w-md w-full shadow-xl" style={{ background: modalBg }}>
            {/* Modal Header */}
            <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: tableBorderColor }}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: isDark ? '#2A1215' : '#FEE2E2' }}>
                  <AlertTriangle size={14} style={{ color: isDark ? '#F87171' : '#DC2626' }} />
                </div>
                <div>
                  <h3 className="text-xs font-semibold" style={{ color: titleColor }}>
                    {t('admin.proxies.deleteModal.title')}
                  </h3>
                  <p className="text-xs" style={{ color: subtitleColor }}>
                    {t('admin.proxies.deleteModal.subtitle')}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletingProxyId(null);
                }}
                className="transition-colors p-1"
                style={{ color: subtitleColor }}
              >
                <X size={14} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5">
              <div className="rounded-md p-3 mb-4" style={{ background: warningBoxBg, borderWidth: 1, borderStyle: 'solid', borderColor: warningBoxBorder }}>
                <div className="flex gap-2">
                  <Info size={14} className="mt-0.5 flex-shrink-0" style={{ color: warningBoxIcon }} />
                  <div>
                    <h4 className="text-xs font-medium mb-1" style={{ color: warningBoxTitle }}>
                      {t('admin.proxies.deleteModal.warning')}
                    </h4>
                    <p className="text-xs" style={{ color: warningBoxText }}>
                      {t('admin.proxies.deleteModal.instruction')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeletingProxyId(null);
                  }}
                  className="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
                  style={{ background: buttonBg, borderWidth: 1, borderStyle: 'solid', borderColor: inputBorder, color: buttonText }}
                >
                  {t('admin.proxies.deleteModal.cancel')}
                </button>
                <button
                  onClick={confirmDeleteProxy}
                  disabled={actionLoading === 'delete'}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                >
                  {actionLoading === 'delete' ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      <span>{t('admin.proxies.deleteModal.deleting')}</span>
                    </>
                  ) : (
                    <>
                      <Trash2 size={14} />
                      <span>{t('admin.proxies.deleteModal.confirm')}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && ReactDOM.createPortal(
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: modalBackdrop, backdropFilter: 'blur(4px)' }}>
          <div className="rounded-lg max-w-md w-full shadow-xl" style={{ background: modalBg }}>
            {/* Modal Header */}
            <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: tableBorderColor }}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: isDark ? '#2A1215' : '#FEE2E2' }}>
                  <AlertTriangle size={14} style={{ color: isDark ? '#F87171' : '#DC2626' }} />
                </div>
                <div>
                  <h3 className="text-xs font-semibold" style={{ color: titleColor }}>
                    {t('admin.proxies.bulkDeleteModal.title')}
                  </h3>
                  <p className="text-xs" style={{ color: subtitleColor }}>
                    {t('admin.proxies.bulkDeleteModal.subtitle', { count: selectedProxies.size })}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowBulkDeleteModal(false)}
                className="transition-colors p-1"
                style={{ color: subtitleColor }}
              >
                <X size={14} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5">
              <div className="rounded-md p-3 mb-4" style={{ background: warningBoxBg, borderWidth: 1, borderStyle: 'solid', borderColor: warningBoxBorder }}>
                <div className="flex gap-2">
                  <Info size={14} className="mt-0.5 flex-shrink-0" style={{ color: warningBoxIcon }} />
                  <div>
                    <h4 className="text-xs font-medium mb-1" style={{ color: warningBoxTitle }}>
                      {t('admin.proxies.bulkDeleteModal.warning')}
                    </h4>
                    <p className="text-xs" style={{ color: warningBoxText }}>
                      {t('admin.proxies.bulkDeleteModal.instruction')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => setShowBulkDeleteModal(false)}
                  className="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
                  style={{ background: buttonBg, borderWidth: 1, borderStyle: 'solid', borderColor: inputBorder, color: buttonText }}
                >
                  {t('admin.proxies.bulkDeleteModal.cancel')}
                </button>
                <button
                  onClick={confirmBulkDelete}
                  disabled={actionLoading === 'bulkDelete'}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                >
                  {actionLoading === 'bulkDelete' ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      <span>{t('admin.proxies.bulkDeleteModal.deleting')}</span>
                    </>
                  ) : (
                    <>
                      <Trash2 size={14} />
                      <span>{t('admin.proxies.bulkDeleteModal.confirm', { count: selectedProxies.size })}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
